"use client";

import React from "react";
import { ArrowRight, Image as ImageIcon } from "lucide-react";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { EcommerceTermsProvider, useEcommerceTerms } from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";
function CategoryCarousel() {
  const {
    terms,
    loading,
    error
  } = useEcommerceTerms();
  if (loading) {
    return <ScrollArea className="w-full">
        <div className="flex w-max space-x-4 pb-6 md:space-x-6">
          {[...Array(5)].map((_, i) => <div key={i} data-index={i} className="w-[280px] shrink-0 sm:w-[320px]">
              <AspectRatio ratio={4 / 5}>
                <div className="h-full w-full animate-pulse rounded-3xl bg-muted/50" />
              </AspectRatio>
            </div>)}
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>;
  }
  if (error) {
    return <div data-section-id="224504" className="flex min-h-[300px] items-center justify-center rounded-3xl border border-border bg-muted/20 p-8 text-center">
        <p className="font-default text-muted-foreground">
          Unable to load collections at this time.
        </p>
      </div>;
  }
  if (terms.length === 0) {
    return <div className="flex min-h-[300px] items-center justify-center rounded-3xl border border-border bg-muted/20 p-8 text-center">
        <p className="font-default text-muted-foreground">No collections found.</p>
      </div>;
  }
  return <ScrollArea className="w-full">
      <div className="flex w-max space-x-4 pb-6 md:space-x-6">
        {terms.map((term, i) => <Link key={term.id} to={term.link || "#"} data-index={i} className="group relative block w-[280px] shrink-0 overflow-hidden rounded-3xl bg-muted sm:w-[320px] shadow-sm transition-shadow duration-500 hover:shadow-xl">
            <AspectRatio ratio={4 / 5}>
              {term.image_url ? <img src={term.image_url} alt={term.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-1000 ease-out group-hover:scale-110" loading="lazy" /> : <div className="absolute inset-0 flex items-center justify-center bg-muted">
                  <ImageIcon className="h-12 w-12 text-muted-foreground/30" />
                </div>}
              
              <div className="absolute inset-0 bg-black/30 transition-colors duration-500 group-hover:bg-black/40" />
              
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center p-6 text-center">
                <div className="flex flex-col items-center transition-transform duration-700 ease-out group-hover:-translate-y-3">
                  <h3 className="font-default text-2xl font-medium tracking-tight text-white md:text-3xl">
                    {term.name}
                  </h3>
                  
                  <div className="mt-4 flex items-center gap-2 opacity-0 transition-all duration-500 group-hover:opacity-100">
                    <span className="font-mono text-[10px] font-medium uppercase tracking-[0.2em] text-white">
                      Explore Ritual
                    </span>
                    <ArrowRight className="h-3.5 w-3.5 text-white" />
                  </div>
                </div>
              </div>
            </AspectRatio>
          </Link>)}
      </div>
      <ScrollBar orientation="horizontal" />
    </ScrollArea>;
}
const wp_term_query_params = {
  taxonomy: "product_cat",
  per_page: 8,
  orderby: "count",
  order: "desc"
};
const wp_term_query = new WP_Term_Query(wp_term_query_params);
export default function ExploreByCategory() {
  return <section id="shop-by-ritual" className="bg-background py-16 text-foreground md:py-32">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 flex flex-col items-start justify-between gap-6 md:mb-20 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <h2 className="font-default text-4xl font-light tracking-tight text-foreground md:text-5xl lg:text-6xl">
              Shop by Ritual
            </h2>
            <p className="mt-6 font-default text-lg leading-relaxed text-muted-foreground/80 md:text-xl">
              Elevate your daily cleansing with our thoughtfully curated collections, 
              designed for the modern home and the art of slow living.
            </p>
          </div>
        </div>

        <EcommerceTermsProvider wp_term_query={wp_term_query}>
          <CategoryCarousel />
        </EcommerceTermsProvider>
      </div>
    </section>;
}